import '@/utils/i18n'
import '@/utils/svg'
import '@/constants/global.css'
import '@/components/sheet'

import { memo, useEffect, useState, type ReactNode } from 'react'
import { Pressable, Text, View } from 'react-native'
import Constants from 'expo-constants'
import { usePathname } from 'expo-router'
import { StatusBar } from 'expo-status-bar'
import { combineProviders } from 'react-combine-provider'
import { SheetProvider } from 'react-native-actions-sheet'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context'

import GraphqlProvider from '@/api/graphql'
import { cn } from '@/utils/cn'
import { installNativeDiagnostics } from '@/utils/nativeDiagnostics'
import { AuthProvider } from '@/stores/auth'
import { ModalProvider, ToastProvider } from '@/components/base'
import { ArrowBack } from '@/components/icon'
import { ImageViewerProvider } from '@/components/ImageViewer'
import { Stack } from '@/components/layout'
import { LoadingIndicatorProvider } from '@/components/LoadingIndicator'
import MaxWidthWrapper from '@/components/MaxWidthWrapper'
import NavigationBar from '@/components/NavigationBar'
import { NavigationManager } from '@/components/NavigationManager'
import { PaymentDetailScreenProvider } from '@/components/orders/PaymentDetailScreen'
import CustomerServiceProvider from '@/components/providers/CustomerService'
import NotificationsProvider from '@/components/providers/notifications'
import { RuntimeEnvironmentProvider } from '@/components/providers/RuntimeEnvironment'
import ScreenHeader from '@/components/ScreenHeader'
import { SecurityVerificationProvider } from '@/components/SecurityVerificationScreen'
import { SelectPaymentMethodProvider } from '@/components/SelectPaymentMethodScreen'
import SplashScreen from '@/components/SplashScreen'
import { SuccessScreenProvider } from '@/components/SuccessScreen'

const CombineProvider = combineProviders([
  CustomerServiceProvider,
  NotificationsProvider,
  SheetProvider,
  PaymentDetailScreenProvider,
  SecurityVerificationProvider,
  SelectPaymentMethodProvider,
  SuccessScreenProvider,
  ImageViewerProvider,
  GraphqlProvider,
  AuthProvider,
  ModalProvider,
  ToastProvider,
  LoadingIndicatorProvider
])

const lightScreens = ['/home', '/scan2pay', '/scan2pay-qr']

installNativeDiagnostics()

export default function RootLayout() {
  useEffect(() => {
    const git = Constants.expoConfig?.extra?.gitInfo
    console.log(JSON.stringify({ frontend: git?.buildId }, null, 2))
  }, [])

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <RuntimeEnvironmentProvider>
          <MaxWidthWrapper>
            <StartupGate
              app={
                <CombineProvider>
                  <StatusBarController />
                  <NavigationManager>
                    <AppStack />
                  </NavigationManager>
                </CombineProvider>
              }
              standaloneApp={
                <CombineProvider>
                  <StatusBarController />
                  <AppStack />
                </CombineProvider>
              }
            />
          </MaxWidthWrapper>
        </RuntimeEnvironmentProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  )
}

function StartupGate({ app, standaloneApp }: { app: ReactNode; standaloneApp: ReactNode }) {
  const pathname = usePathname()
  const [isStandalone] = useState(pathname === '/scan2pay-qr')
  const [isReady, setIsReady] = useState(isStandalone)

  if (!isReady) {
    return (
      <>
        <StatusBar />
        <SplashScreen onFinish={() => setIsReady(true)} />
      </>
    )
  }

  return <>{isStandalone ? standaloneApp : app}</>
}

function StatusBarController() {
  const pathname = usePathname()
  return <StatusBar style={lightScreens.includes(pathname) ? 'light' : 'dark'} />
}

const AppStack = memo(function AppStack() {
  const { t } = useTranslation()

  return (
    <Stack
      initialRouteName={'index'}
      screenOptions={{
        // Page content is ready at the time of navigation; a stack transition
        // only delays feedback on lower-end native devices.
        animation: 'none',
        header: (props) => <Header {...props} />
      }}
    >
      <Stack.Screen name={'index'} options={{ headerShown: false }} />
      <Stack.Screen name={'(main)/(tabs)'} options={{ headerShown: false }} />
      <Stack.Screen name={'scan2pay-qr'} options={{ header: () => <NavigationBar>{t('ui:scanToPay')}</NavigationBar> }} />

      {/* HACK, android require preset options */}
      {/* {modalNames.map((name) => (
        <Stack.Screen
          key={name}
          name={name}
          options={{ presentation: 'modal', gestureEnabled: true, ...TransitionPresets.ModalPresentationIOS }}
        />
      ))} */}
    </Stack>
  )
})

function Header({
  navigation,
  route
}: {
  navigation: { canGoBack: () => boolean; goBack: () => void }
  route: { name: string }
}) {
  const { top } = useSafeAreaInsets()

  return (
    <View className={'bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={
          navigation.canGoBack() && (
            <Pressable className={'p-2'} onPress={navigation.goBack}>
              {({ pressed }) => <ArrowBack className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
            </Pressable>
          )
        }
        centerElement={<Text className={'text-base font-bold text-text-primary'}>{route.name}</Text>}
      />
    </View>
  )
}
